import { chartColors } from '../../../../shared/common/chart-colors';
import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/nb'; // Import Norwegian locale
import 'moment/locale/sv'; // Import Swedish locale
import 'moment/locale/pl'; // Import Polish locale
import { ActivityServices } from '../../services/activities.services';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { ActivityMetadataDto } from 'src/app/kuba/shared/models/pagedResponse';
import { ActivityMetadataFilterDto } from '../../models/activityMetadataFilterRequest';

@Component({
  selector: 'app-activity-list-chart',
  templateUrl: './activity-list-chart.component.html',
  styleUrls: ['./activity-list-chart.component.css'],
  standalone: false
})
export class ActivityListChartComponent implements OnInit, OnChanges {
  
  @Input() metadataFilter: ActivityMetadataFilterDto | null = null;
  
  activitiesMetadata: ActivityMetadataDto;
  hasData: boolean = true; 

  multi: any[] = [];
  view: any[];

  // Chart options
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = false;
  xAxisLabel = 'Month';
  showYAxisLabel = false;
  yAxisLabel: string;
  legendTitle: string;
  animations = true;


  colorScheme = {
    domain: [chartColors.done, chartColors.open, chartColors.rejected, chartColors.danger]
  };

  constructor(private translate: TranslateService, private activityService: ActivityServices) {
    this.setMomentLocale();
    this.yAxisLabel = this.translate.instant('ACTIVITIES');
    this.legendTitle = this.translate.instant('DESCRIPTION');
    this.customColors = this.customColors.bind(this);
  }

  ngOnInit(): void {
    this.loadActivitiesMetadata();

    this.translate.onLangChange.subscribe(() => {
      this.setMomentLocale();
      this.updateChartData(); // Update chart data when language changes
    });
  }

  /**
   * Maps the current translate language to a moment locale
   */
  private setMomentLocale(): void {
    const langMap: { [key: string]: string } = {
      'en': 'en',
      'no': 'nb',
      'sv': 'sv',
      'pl': 'pl'
    };
    const currentLang = this.translate.currentLang() || this.translate.fallbackLang() || 'en';
    moment.locale(langMap[currentLang] || currentLang);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload metadata when filter changes
    if (changes['metadataFilter'] && !changes['metadataFilter'].firstChange) {
      this.loadActivitiesMetadata();
    }
  }

  onSelect(event) {
    console.log('[Chart] Bar selected:', event);
  }

  updateChartData(): void {
    if (!this.activitiesMetadata || !this.activitiesMetadata.MonthlyData) {
      this.multi = [];
      this.hasData = false;
      return;
    }

    // Check if there's any data in the monthly data
    const hasAnyData = this.activitiesMetadata.MonthlyData.some(month => 
      month.OpenCount > 0 || month.DoneCount > 0 || 
      month.RejectedCount > 0 || month.OverDeadlineCount > 0
    );
    
    this.hasData = hasAnyData;

    const monthlyData = this.activitiesMetadata.MonthlyData;
    const statusCategories = ['REJECTED', 'DONE', 'OPEN'];
    const translatedStatusCategories = statusCategories.map(status =>
      this.translate.instant(status.toUpperCase())
    );
    const chartData = monthlyData.map(month => {
      // Calculate adjusted open count (exclude overdue activities to avoid double counting)
      const adjustedOpenCount = month.OpenCount - month.OverDeadlineCount;
      
      // Derive localized month name from the Month field (e.g., "2024-01") with capitalized first letter
      const monthName = moment(month.Month, 'YYYY-MM').format('MMMM');
      const monthNameOnly = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      
      const item = {
        name: monthNameOnly,
        series: [
          {
            name: translatedStatusCategories[2], // OPEN (adjusted to exclude overdue)
            value: Math.max(0, adjustedOpenCount), // Ensure non-negative value
            extra: { isExpired: false }
          },
          {
            name: translatedStatusCategories[1], // DONE
            value: month.DoneCount,
            extra: { isExpired: false }
          },
          {
            name: translatedStatusCategories[0], // REJECTED
            value: month.RejectedCount,
            extra: { isExpired: false }
          },
          {
            name: this.translate.instant('OverDeadlineCount'),
            value: month.OverDeadlineCount,
            extra: { isExpired: true }
          }
        ]
      };
      return item;
    });
    this.multi = chartData;
  }

  customColors(name: string, extra: any): string {
    if (extra?.isExpired) {
      return chartColors.danger;
    }
    switch (name) {
      case this.translate.instant('DONE'):
        return chartColors.done;
      case this.translate.instant('OPEN'):
        return chartColors.open;
      case this.translate.instant('REJECTED'):
        return chartColors.rejected;
      case this.translate.instant('OverDeadlineCount'):
        return chartColors.danger;
      default:
        return chartColors.rejected; // default color
    }
  }

  yAxisTickFormatting(value: number): string {
    return Number.isInteger(value) ? value.toString() : '';
  }

   loadActivitiesMetadata(): void {
    // For portal users, pass portalId for security
    const portalId = BaseServices.UserRole === 'Portal' ? BaseServices.PortalId : undefined;
    
    // Use filtered metadata if filter is provided, otherwise use regular metadata
    const metadataObservable = this.metadataFilter 
      ? this.activityService.getFilteredActivitiesMetadata(BaseServices.BusinessId, BaseServices.UserRole, this.metadataFilter, portalId)
      : this.activityService.getActivitiesMetadata(BaseServices.BusinessId, BaseServices.UserRole, portalId);
    
    metadataObservable.subscribe(data => {
        this.activitiesMetadata = data;
        this.updateChartData();
      },error => {
        console.error('[Chart] Error loading activities metadata:', error);
      }
    );
  }

}
